import React, { Suspense, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { setUserContext } from '../../../global/ducks/app-context';
import { UserContext } from '../../../global/models/app-context-model';
import { DemographicAppKeys, DemographicsContext } from '../../../global/models/demographics-model';
import { TcsmEpiApiState, TcsmStates } from '../../../root/tcsm-model';
import { ArcRoutes } from '../../arc/constants/routes';
import { Compensation } from '../../arc/features/compensation/compensation';
import { Employment } from '../../arc/features/employment/employment';
import { EmploymentState } from '../../arc/features/employment/employment-model';
import { AccountInfoRoutes } from '../constants/routes';
import { DependentInfo } from '../features/dependents/dependent-info';
import { MaritalStatusApp } from '../features/marital-status/marital-status';
import { PersonalInformationApp } from '../features/personal-info/personal-info';
import { MyRelationshipsApp } from '../features/relationships/my-relationships';
import { useAccountInfoSettings } from '../hooks/useAccountInfoSettings';
import AccountInfoContainer from './account-info-container';
import { useDemographicsPerson } from '../hooks/useDemographicsPerson';
import { useBasePath } from '../../../global/hooks/useBasePath';
import { withForbidden } from '../../../global/components/HOCs/with-forbidden';
import { TcsmFeatures } from '../../../global/models/features-model';
import { withLayout } from '../../../global/components/HOCs/with-layout';
import AnnualEnrollmentApp from '../../annual-enrollment/components/annual-enrollment-app';
import { AnnualEnrollmentRoutes } from '../../annual-enrollment/constants/annual-enrollment-routes';
import { InfoBarWrapper } from '../../../global/components/layout/info-bar-wrapper';
import { NameClientNumber } from '../../../global/components/name-client-number';
import { Breadcrumbs } from '../../../global/components/breadcrumbs';
import { PersonalInfoState } from '../features/personal-info/personal-info-models';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { GpsType, LayoutModels } from '../../../global/components/layout/layout-models';
import { PensionEnrollmentApp } from '../../arc/features/pension-enrollment/pension-enrollment-app';
import { EmploymentTerminationApp } from '../../arc/features/termination/employment-termination-app';
import AnnualEnrollmentProgressBar from '../../annual-enrollment/components/annual-enrollment-progress';
import { NotificationAlertPage } from '../../notification/notification-page';
import { MidYearApp } from '../../arc/features/mid-year/mid-year-app';
import { withParticipantSelect } from '../../../global/components/HOCs/with-participant-select';
import { GenericDocumentUploadPage } from '../../generic-doc-upload/generic-document-upload';
import { GenericDocumentUpload } from '../features/personal-info/document-upload-models';
import { PageFactory } from '../../../global/components/page-factory';
import { EmploymentCompensation } from '../../arc/features/employment-compensation/employment-compensation';
import { shouldShowAeHeader } from '../../annual-enrollment/helpers/annual-enrollment-helpers';
import { BenefitSurvey } from '../features/benefit-survey/benefit-survey';
import { withInstitutionSelect } from '../../../global/components/HOCs/with-institution-select';
import MyDocumentsList from '../../my-documents/my-documents-list';
import { TcsmAppConfigs } from '../../../global/models/configuration-models';
import { BeneficiaryLanding } from '../../arc/features/beneficiary/beneficiary-landing-page';
import { BeneficiaryUpdate } from '../../arc/features/beneficiary/beneficiary-update';
import { BeneficiaryCreate } from '../../arc/features/beneficiary/create-beneficiary/create-beneficiary-page';
import { LifeEventsLanding } from '../features/life-events/life-events-landing';
import MyCPGMidyearApp from '../features/mycpg-midyear/mycpg-midyear-app';
import { shouldShowMidYearHeader } from '../features/mycpg-midyear/helpers/mycpg-midyear-stepper-helpers';
import { useMyCPGMidYearSettings } from '../features/mycpg-midyear/hooks/useMyCPGMidyearSettings';
import { useChurchEmployees } from '../../../global/hooks/useChurchEmployees';
import { getBasePath } from '../../../global/helpers/routing';

const ClergyBio = React.lazy(() => import('../../clergy-bio/clergy-bio-app'));
const MemberNewHireApp = React.lazy(() => import('../../arc/features/new-hire-enrollment/member/member-new-hire-app'));
const DomesticPartner = React.lazy(() => import('../features/domestic-partner/domestic-partner'));
const QualifyingEvent = React.lazy(() => import('../features/qualifying-event/qualifying-event'));
const DeathOfChild = React.lazy(() => import('../features/death-of-child/death-of-child'));

// important to keep these outside of the PersonManagementApp so they don't get re-mounted on every render
const CompensationWrapper = withLayout()(withForbidden(TcsmFeatures.Compensation)(AccountInfoContainer));
const EmploymentWrapper = withLayout()(withForbidden(TcsmFeatures.Employment)(AccountInfoContainer));
const RelationshipsWrapper = withLayout(null, GpsType.RELATIONSHIP)(withForbidden(TcsmFeatures.Relationships)(AccountInfoContainer));
const PersonalInfoWrapper = withLayout(null, GpsType.DEMOGRAPHIC)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));
const MaritalStatusWrapper = withLayout(null, GpsType.MARITAL_STATUS)(withForbidden(TcsmFeatures.MaritalStatus)(AccountInfoContainer));
const DependentsWrapper = withLayout(LayoutModels.NO_LAY_OUT)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));
const PensionEnrollmentWrapper = withLayout(null, GpsType.PENSION_ENROLLMENT)(withForbidden(TcsmFeatures.PensionEnrollment)(AccountInfoContainer));
const TerminationWrapper = withLayout(null, GpsType.TERMINATION_FORM)(withForbidden(TcsmFeatures.Termination)(AccountInfoContainer));
const LoaWrapper = withLayout(null, GpsType.LOA_FORM)(withForbidden(TcsmFeatures.Termination)(AccountInfoContainer));
const MyDocumentsWrapper = withLayout(LayoutModels.FULL_WIDTH)(withForbidden(TcsmFeatures.MyDocuments)(AccountInfoContainer));

const MidYearWrapper = withParticipantSelect()(MidYearApp);
const BenefitSurveyApp = withInstitutionSelect()(BenefitSurvey);

interface Props {
	appConfig: TcsmAppConfigs;
	isArc: boolean;
}

export const PersonManagementApp = (props: Props) => {
	const { appConfig } = props;
	const app = useContext(DemographicsContext);
	const routeMatch = useRouteMatch();
	const location = useLocation();
	const demographicsSettingsLoaded = useAccountInfoSettings(appConfig.personalInfoSettingId, appConfig.relationshipsSettingId);
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
	const personLoaded = useDemographicsPerson(app);
	const employeeSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.EmployeeSearch}`);
	const participantSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.Participants}`);
	const groupAdminSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.GroupAdministrators}`);
	const dispatch = useDispatch();
	const personState = useSelector<TcsmStates, PersonalInfoState>(state => state.personalInfo[app]);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(a => a.epiProps?.labelSettingState?.epiContent);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const history = useHistory();
	const midyearLoaded = useMyCPGMidYearSettings(appConfig.myCpgMidYearSettingsId);
	const basePath = getBasePath(appConfig.currentCulture);

	const isChurchEmployee = useChurchEmployees();
	useEffect(() => {
		if (app === DemographicAppKeys.myCPG) {
			dispatch(setUserContext(UserContext.MY_CPG));
		}
	}, [app]);

	if (app === DemographicAppKeys.arc && !employmentState?.currentEmployee) {

		const currentPath = location?.pathname;
		let personSelectRoute = employeeSelect;

		if (currentPath?.includes(`${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.Participants}`)) {
			personSelectRoute = participantSelect;
		}

		if (currentPath?.includes(`${ArcRoutes.GroupsLanding}${ArcRoutes.GroupAdministrators}`)) {
			personSelectRoute = groupAdminSelect;
		}

		return (
			<Redirect to=
				{
					{
						pathname: personSelectRoute,
						state: {
							actionLink: location.pathname,
						},
					}
				}
			/>);
	}

	if (!demographicsSettingsLoaded || !personLoaded || !midyearLoaded) {
		return <LoadingCircle />;
	}

	if (!isChurchEmployee && app === DemographicAppKeys.myCPG) {
		return <Redirect to={`${basePath}/${AccountInfoRoutes.MyCpgRoot}`} />;
	}

	function arcSpecificRoutes() {
		if (props.isArc) {
			return (
				<>
					<Route path={`${routeMatch.path}${ArcRoutes.Compensation}`} render={() => (
						<CompensationWrapper basePath={routeMatch.path}>
							<Compensation />
						</CompensationWrapper>
					)} />
					<Route path={`${routeMatch.path}${ArcRoutes.Employment}`} render={() => (
						<EmploymentWrapper basePath={routeMatch.path}>
							<Employment />
						</EmploymentWrapper>
					)} />

					<Route path={`${routeMatch.path}${ArcRoutes.Coverage}`} render={() => (
						<MidYearWrapper annualEnrollmentSettingsId={appConfig.annualEnrollmentSettingsId} />
					)} />

					<Route exact path={`${routeMatch.path}${ArcRoutes.PensionEnrollment}`}
						render={() => (
							<PensionEnrollmentWrapper basePath={routeMatch.path}>
								<PensionEnrollmentApp
									settingsId={appConfig.pensionEnrollmentSettingsId}
								/>
							</PensionEnrollmentWrapper>
						)}
					/>
					<Route exact path={`${routeMatch.path}${ArcRoutes.EmploymentTermination}`}
						render={() => (
							<TerminationWrapper>
								<EmploymentTerminationApp terminationSettingsId={appConfig.terminationSettingsId} />
							</TerminationWrapper>
						)}
					/>
					<Route exact path={`${routeMatch.path}${ArcRoutes.UnpaidLeaveOfAbsence}`}
						render={() => (
							<LoaWrapper>
								<EmploymentTerminationApp terminationSettingsId={appConfig.terminationSettingsId} isLoa />
							</LoaWrapper>
						)}
					/>
					<Route exact path={`${routeMatch.path}${ArcRoutes.DocumentUpload}`}
						render={() => <GenericDocumentUploadPage uploadType={GenericDocumentUpload.AdminDocUpload} />}
					/>

					<Route exact path={`${routeMatch.path}${ArcRoutes.CompensationEmployment}`}
						render={() => (
							<EmploymentCompensation />
						)}
					/>

					<Route path={`${routeMatch.path}${ArcRoutes.BenefitSurvey}`}
						render={() => (
							<BenefitSurveyApp />
						)} />					
				</>
			);
		}

		return null;

	}
	const breadCrumbInfoBar = (hideBreadcrumbs: boolean) => {
		return app === DemographicAppKeys.myCPG &&
			<>
				{!hideBreadcrumbs && <Breadcrumbs routeBreadcrumbs={epiProps?.siteSettingsState?.siteSettings?.routeBreadcrumbs} />}
				<InfoBarWrapper>
					<NameClientNumber client={personState.currentPerson} clientNumberLabel={labelSettings?.clientNumberLabel} />
					{
						shouldShowAeHeader(history) &&
						<Grid item md={4} className='infobar-nav'>
							<h2>{epiProps?.annualEnrollmentSettingsState?.epiContent?.annualEnrollmentTitle}</h2>
						</Grid>
					}
					{
						shouldShowMidYearHeader(history) &&
						<Grid item md={4} className='infobar-nav'>
							<h2>{epiProps?.myCpgMidyearSettingsState?.epiContent?.midYearStepperTitle}</h2>
						</Grid>
					}
				</InfoBarWrapper>
				<AnnualEnrollmentProgressBar basePath={`${routeMatch.path}${AnnualEnrollmentRoutes.AnnualEnrollment}`} />
			</>;
	};
	function myCpgSpecificRoutes() {
		if (!props.isArc) {
			return (
				<>
					<Switch>
						<Route
							path={`${routeMatch.path}${AnnualEnrollmentRoutes.AnnualEnrollment}`}
							render={() => (
								<>
									{breadCrumbInfoBar(true)}
									<DemographicsContext.Provider value={DemographicAppKeys.myCPG}>
										<AnnualEnrollmentApp
											annualEnrollmentSettingId={appConfig.annualEnrollmentSettingsId}
											personalInfoSettingId={appConfig.personalInfoSettingId}
											relationshipsSettingId={appConfig.relationshipsSettingId}
										/>
									</DemographicsContext.Provider>
								</>
							)}
						/>
						<Route exact path={`${routeMatch.path}${ArcRoutes.Notifications}`}
							render={() => (
								<>
									{breadCrumbInfoBar(false)}
									<NotificationAlertPage />
								</>
							)}
						/>
						<Route exact path={`${routeMatch.path}${ArcRoutes.DocumentUpload}`}
							render={() => <GenericDocumentUploadPage uploadType={GenericDocumentUpload.MemberDocUpload} />}
						/>
						<Route exact path={`${routeMatch.path}${AccountInfoRoutes.MyDocuments}`}
							render={() => (
								<>
									{breadCrumbInfoBar(false)}
									<DemographicsContext.Provider value={DemographicAppKeys.myCPG}>
										<MyDocumentsWrapper basePath={routeMatch.path}>
											<MyDocumentsList searchSettingsId={appConfig.searchSettingsId} />
										</MyDocumentsWrapper>
									</DemographicsContext.Provider>
								</>
							)}
						/>

						<Route path={`${routeMatch.path}${ArcRoutes.ClergyBio}`}
							render={() => (
								<ClergyBio settingsId={appConfig?.clergyBioSettingsId} />
							)} />

						<Route path={`${routeMatch.path}${ArcRoutes.MemberNewHireEnrollment}`}>
							<>
								<MemberNewHireApp appSettings={appConfig} />
							</>
						</Route>
						<Route exact path={`${routeMatch.path}${ArcRoutes.Beneficiary}/:plan`}
							render={() => (
								<>
									{breadCrumbInfoBar(false)}
									<BeneficiaryUpdate settingsId={appConfig?.beneficiarySettingsId} />
								</>
							)}
						/>
						<Route exact path={`${routeMatch.path}${ArcRoutes.Beneficiary}`}
							render={() => (
								<>
									{breadCrumbInfoBar(false)}
									<BeneficiaryLanding settingsId={appConfig?.beneficiarySettingsId} />
								</>
							)}
						/>
						<Route exact path={`${routeMatch.path}${ArcRoutes.LifeEvents}`}
							render={() => (
								<>
									{breadCrumbInfoBar(false)}
									<LifeEventsLanding appConfig={appConfig}/>

								</>
							)}
						/>						
						<Route exact path={`${routeMatch.path}${AccountInfoRoutes.PersonalInfoSegment}${ArcRoutes.Beneficiary}`}
							render={() => (
								<>
									{breadCrumbInfoBar(false)}
									<BeneficiaryCreate settingsId={appConfig?.beneficiarySettingsId} />
								</>
							)}
						/>
						<Route path={`${routeMatch.path}${AccountInfoRoutes.Midyear}`}
							render={() => (
								<>
									{breadCrumbInfoBar(true)}
									<MyCPGMidyearApp appConfig={appConfig} />
								</>
							)}
						/>
						<Route path={`${routeMatch.path}${ArcRoutes.QualifyingEvent}`}
							render={() => (
								<>
									{breadCrumbInfoBar(true)}
									<QualifyingEvent
										appConfig={appConfig}
									/>
								</>
							)}
						/>						
						<Route render={() => <PageFactory />} />
					</Switch>
				</>
			);
		}
		return null;
	}
	return (
		<>
			<Suspense fallback={<LoadingCircle />}>
				<Switch>
					<Route exact path={`${routeMatch.path}${AccountInfoRoutes.PersonalInfoSegment}`} render={() => (
						<>
							{breadCrumbInfoBar(false)}
							<PersonalInfoWrapper basePath={routeMatch.path}>
								<PersonalInformationApp />
							</PersonalInfoWrapper>
						</>
					)} />
					<Route exact path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} render={() => (
						<>
							{breadCrumbInfoBar(false)}
							<RelationshipsWrapper basePath={routeMatch.path}>
								<MyRelationshipsApp />
							</RelationshipsWrapper>
						</>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`} render={() => (
						<>
							{breadCrumbInfoBar(false)}
							<MaritalStatusWrapper>
								<MaritalStatusApp basePath={routeMatch.path} />
							</MaritalStatusWrapper>
						</>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DependentSegment}`} render={() => (
						<>
							{breadCrumbInfoBar(false)}
							<DependentsWrapper basePath={routeMatch.path}>
								<DependentInfo relationshipsPageRoute={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} />
							</DependentsWrapper>
						</>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DomesticPartner}`} render={() => (
						<>
							{breadCrumbInfoBar(false)}
							<DomesticPartner basePath={routeMatch.path} />
						</>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DeathOfChild}`} render={() => (
						<>
							{breadCrumbInfoBar(false)}
							<DeathOfChild />
						</>
					)} />
					{myCpgSpecificRoutes()}
					{arcSpecificRoutes()}
				</Switch>
			</Suspense>
		</>
	);
};

